import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Trash2, Plus, Package, FileText, RefreshCw } from 'lucide-react';
import {
  fetchItems,
  fetchBranches,
  fetchSettings,
  createSupplierInvoiceWithItems
} from '@/api';
import { cn } from '@/lib/utils';
import type { Supplier } from '@/types/supplier';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { format, addDays } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const SKU_MAX_LENGTH = 8;
const AUTO_SKU_LENGTH = 5;
const SKU_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

const generateShortSku = () =>
  Array.from({ length: AUTO_SKU_LENGTH }, () =>
    SKU_CHARACTERS[Math.floor(Math.random() * SKU_CHARACTERS.length)]
  ).join('');
const isLegacyAutoSku = (sku: string) => /^NEW-\d+$/i.test(sku.trim());

interface AddInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  suppliers: Supplier[];
}

interface AddInvoiceFormProps {
  suppliers: Supplier[];
  onSuccess: () => void;
  onCancel?: () => void;
}

interface Item {
  id: string;
  name: string;
  sku: string;
  buying_price: number;
  selling_price: number;
  category?: string;
  quantity: number;
  barcode?: string | null;
  vat_enabled?: number | boolean;
}

interface Branch {
  id: string;
  name: string;
  is_main?: boolean;
}

interface InvoiceLineItem {
  id: string;
  item_id?: string;
  item_name: string;
  item_sku: string;
  barcode?: string;
  vat_enabled?: boolean;
  quantity: number;
  buy_price: number;
  sell_price: number;
  total: number;
  is_new: boolean;
}

interface EntryRow {
  item_id?: string;
  item_name: string;
  item_sku: string;
  barcode: string;
  vat_enabled: boolean;
  quantity: number;
  buy_price: number;
  sell_price: number;
}

const normalizeItems = (itemsData: any[]): Item[] =>
  itemsData.map((item: any) => ({
    ...item,
    buying_price: parseFloat(item.buying_price) || 0,
    selling_price: parseFloat(item.selling_price) || 0,
    quantity: parseInt(item.quantity, 10) || 0,
    barcode: item.barcode ?? '',
    vat_enabled: item.vat_enabled ?? 0
  }));

export function AddInvoiceForm({ suppliers, onSuccess, onCancel }: AddInvoiceFormProps) {
  const currencySymbol = useCurrencySymbol();
  const [initialInvoiceDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // Invoice header fields
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(initialInvoiceDate);
  const [dueDate, setDueDate] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [showAttachment, setShowAttachment] = useState(false);
  const [vatRate, setVatRate] = useState(0);
  const [transportationCost, setTransportationCost] = useState<number>(0);

  // Line items
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

  useEffect(() => {
    let changed = false;
    const normalized = lineItems.map((item) => {
      if (isLegacyAutoSku(item.item_sku)) {
        changed = true;
        return { ...item, item_sku: generateShortSku() };
      }
      return item;
    });

    if (changed) {
      setLineItems(normalized);
    }
  }, [lineItems]);

  const [entryRow, setEntryRow] = useState<EntryRow>({
    item_id: '',
    item_name: '',
    item_sku: '',
    barcode: '',
    vat_enabled: false,
    quantity: 1,
    buy_price: 0,
    sell_price: 0
  });
  const [entryActiveField, setEntryActiveField] = useState<'name' | 'sku' | 'barcode' | null>(null);
  const itemNameInputRef = useRef<HTMLInputElement>(null);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [autoFillNonce, setAutoFillNonce] = useState(() => Math.random().toString(36).slice(2, 8));
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [conflictSku, setConflictSku] = useState<string | null>(null);
  const [conflictBarcode, setConflictBarcode] = useState<string | null>(null);
  const toNumberInputValue = (value: number) => (Number.isFinite(value) ? String(value) : '');
  const safeNumber = (value: number) => (Number.isFinite(value) ? value : 0);
  const totalItemQuantity = lineItems.reduce((sum, item) => sum + safeNumber(item.quantity), 0);
  const transportationCostAmount = safeNumber(transportationCost);
  const transportationCostPerUnit = totalItemQuantity > 0 ? transportationCostAmount / totalItemQuantity : 0;
  const hasUnsavedChanges = Boolean(
    supplierId ||
    invoiceNumber.trim() ||
    invoiceDate !== initialInvoiceDate ||
    dueDate ||
    selectedBranch ||
    attachment ||
    notes.trim() ||
    showAttachment ||
    transportationCostAmount > 0 ||
    lineItems.length > 0 ||
    entryRow.item_name.trim() ||
    entryRow.item_sku.trim() ||
    entryRow.barcode.trim() ||
    entryRow.quantity !== 1 ||
    safeNumber(entryRow.buy_price) > 0 ||
    safeNumber(entryRow.sell_price) > 0 ||
    entryRow.vat_enabled
  );
  const { confirmNavigation, confirmationDialog, proceedWithoutPrompt } = useUnsavedChangesGuard({
    when: hasUnsavedChanges && !loading,
  });

  // Load items and branches on mount
  useEffect(() => {
    loadData();
    setAutoFillNonce(Math.random().toString(36).slice(2, 8));
  }, []);

  // Auto-calculate due date based on supplier payment terms
  useEffect(() => {
    if (supplierId && invoiceDate) {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier) {
        const days = supplier.paymentTerm === '15days' ? 15 : supplier.paymentTerm === '30days' ? 30 : 0;
        const [year, month, day] = invoiceDate.split('-').map(Number);
        setDueDate(format(addDays(new Date(year, month - 1, day), days), 'yyyy-MM-dd'));
      }
    }
  }, [supplierId, invoiceDate, suppliers]);

  const loadData = async () => {
    try {
      const [itemsRes, branchesRes, settingsRes] = await Promise.all([
        fetchItems(),
        fetchBranches(),
        fetchSettings()
      ]);
      const parsedItems = normalizeItems(itemsRes.data || []);
      setItems(parsedItems);
      setBranches(branchesRes.data || []);
      const rawVatRate = settingsRes?.data?.vat_percentage ?? settingsRes?.data?.vatPercentage ?? 0;
      setVatRate(parseFloat(rawVatRate) || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load items and branches');
    }
  };
  const computeLineTotal = (quantity: number, buyPrice: number, vatEnabled?: boolean) => {
    const subtotal = safeNumber(quantity) * safeNumber(buyPrice);
    if (!vatEnabled || vatRate <= 0) return subtotal;
    return subtotal + (subtotal * vatRate) / 100;
  };

  // Filter items based on search term
  const entryQuery = useMemo(() => {
    if (entryActiveField === 'sku') {
      return entryRow.item_sku.trim();
    }
    if (entryActiveField === 'barcode') {
      return entryRow.barcode.trim();
    }
    return entryRow.item_name.trim();
  }, [entryActiveField, entryRow.barcode, entryRow.item_name, entryRow.item_sku]);

  const entrySuggestions = useMemo(() => {
    if (!entryQuery) return [];
    const term = entryQuery.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(term) ||
      item.sku.toLowerCase().includes(term) ||
      (item.barcode || '').toLowerCase().includes(term)
    ).slice(0, 12);
  }, [items, entryQuery]);

  const displaySuggestions = useMemo(() => {
    if (entrySuggestions.length >= 5) return entrySuggestions;
    const padded = [...entrySuggestions];
    while (padded.length < 5) {
      padded.push(null as unknown as Item);
    }
    return padded;
  }, [entrySuggestions]);

  useEffect(() => {
    setSuggestionIndex(0);
  }, [entryQuery]);

  useEffect(() => {
    if (!suggestionsRef.current) return;
    const target = suggestionsRef.current.querySelector<HTMLButtonElement>(
      `[data-suggestion-index="${suggestionIndex}"]`
    );
    target?.scrollIntoView({ block: 'nearest' });
  }, [suggestionIndex, entrySuggestions.length]);

  // Calculate totals
  const invoiceSubtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + item.total, 0);
  }, [lineItems]);
  const invoiceTotal = invoiceSubtotal + transportationCostAmount;

  const resetEntryRow = () => {
    setEntryRow({
      item_id: '',
      item_name: '',
      item_sku: '',
      barcode: '',
      vat_enabled: false,
      quantity: 1,
      buy_price: Number.NaN,
      sell_price: Number.NaN
    });
    setEntryActiveField(null);
  };

  const setEntryFromItem = (item: Item) => {
    setEntryRow({
      item_id: item.id,
      item_name: item.name,
      item_sku: item.sku,
      barcode: item.barcode ?? '',
      vat_enabled: Boolean(item.vat_enabled),
      quantity: 1,
      buy_price: item.buying_price,
      sell_price: item.selling_price
    });
    setEntryActiveField(null);
    setSuggestionIndex(0);
  };

  const resolveItemMatch = () => {
    const skuTerm = entryRow.item_sku.trim().toLowerCase();
    const barcodeTerm = entryRow.barcode.trim().toLowerCase();
    const nameTerm = entryRow.item_name.trim().toLowerCase();

    if (entryRow.item_id) {
      return items.find(item => item.id === entryRow.item_id);
    }
    if (skuTerm) {
      const skuMatch = items.find(item => item.sku.toLowerCase() === skuTerm);
      if (skuMatch) return skuMatch;
    }
    if (barcodeTerm) {
      const barcodeMatch = items.find(item => (item.barcode || '').toLowerCase() === barcodeTerm);
      if (barcodeMatch) return barcodeMatch;
    }
    if (nameTerm) {
      return items.find(item => item.name.toLowerCase() === nameTerm);
    }
    return undefined;
  };

  const findDraftConflicts = (lineItemsToCheck: InvoiceLineItem[]) => {
    const seenSku = new Map<string, InvoiceLineItem>();
    const seenBarcode = new Map<string, InvoiceLineItem>();

    for (const item of lineItemsToCheck) {
      const sku = item.item_sku.trim().toLowerCase();
      const barcode = (item.barcode || '').trim().toLowerCase();

      if (sku && seenSku.has(sku)) {
        return { type: 'sku', value: item.item_sku };
      }
      if (barcode && seenBarcode.has(barcode)) {
        return { type: 'barcode', value: item.barcode || '' };
      }

      if (sku) {
        seenSku.set(sku, item);
      }
      if (barcode) {
        seenBarcode.set(barcode, item);
      }
    }

    return null;
  };

  const findCatalogConflicts = (catalogItems: Item[], lineItemsToCheck: InvoiceLineItem[]) => {
    const skuMap = new Map(
      catalogItems
        .filter(item => item.sku)
        .map(item => [item.sku.trim().toLowerCase(), item.name])
    );
    const barcodeMap = new Map(
      catalogItems
        .filter(item => item.barcode)
        .map(item => [item.barcode!.trim().toLowerCase(), item.name])
    );

    for (const item of lineItemsToCheck) {
      const sku = item.item_sku.trim().toLowerCase();
      const barcode = (item.barcode || '').trim().toLowerCase();

      if (!item.item_id && sku && skuMap.has(sku)) {
        return {
          type: 'sku',
          value: item.item_sku,
          itemName: skuMap.get(sku) || 'existing item'
        };
      }
      if (!item.item_id && barcode && barcodeMap.has(barcode)) {
        return {
          type: 'barcode',
          value: item.barcode || '',
          itemName: barcodeMap.get(barcode) || 'existing item'
        };
      }
    }

    return null;
  };

  const handleCommitEntryRow = () => {
    const matchedItem = resolveItemMatch();
    const skuTerm = entryRow.item_sku.trim().toLowerCase();
    const barcodeTerm = entryRow.barcode.trim().toLowerCase();
    const manualSku = entryRow.item_sku.trim();
    if (!matchedItem && !isLegacyAutoSku(manualSku) && manualSku.length > SKU_MAX_LENGTH) {
      toast.error('SKU must be 8 characters or fewer');
      return;
    }

    const fallbackSku = matchedItem?.sku || generateShortSku();
    const resolvedSku = manualSku && !isLegacyAutoSku(manualSku) ? manualSku : fallbackSku;
    const resolvedBarcode = entryRow.barcode.trim() || matchedItem?.barcode || '';
    const draft = lineItems.concat([{
      id: 'tmp-draft',
      item_id: matchedItem?.id,
      item_name: entryRow.item_name.trim() || matchedItem?.name || '',
      item_sku: resolvedSku,
      barcode: resolvedBarcode,
      vat_enabled: entryRow.vat_enabled || Boolean(matchedItem?.vat_enabled),
      quantity: entryRow.quantity,
      buy_price: entryRow.buy_price > 0 ? entryRow.buy_price : matchedItem?.buying_price || 0,
      sell_price: entryRow.sell_price > 0 ? entryRow.sell_price : matchedItem?.selling_price || 0,
      total: (entryRow.buy_price > 0 ? entryRow.buy_price : matchedItem?.buying_price || 0) * entryRow.quantity,
      is_new: !matchedItem
    }]);

    const catalogConflict = findCatalogConflicts(items, draft);
    if (catalogConflict) {
      if (catalogConflict.type === 'sku') {
        setConflictSku(catalogConflict.value);
        toast.error(`SKU already exists as "${catalogConflict.itemName}". Choose that item or use a different SKU.`);
      } else {
        setConflictBarcode(catalogConflict.value);
        toast.error(`Barcode already exists as "${catalogConflict.itemName}".`);
      }
      return;
    }

    const draftConflict = findDraftConflicts(draft);
    if (draftConflict) {
      if (draftConflict.type === 'sku') {
        setConflictSku(draftConflict.value);
        toast.error(`Duplicate SKU in invoice: "${draftConflict.value}". Use a unique SKU.`);
      } else {
        setConflictBarcode(draftConflict.value);
        toast.error(`Duplicate barcode in invoice: "${draftConflict.value}". Use a unique barcode.`);
      }
      return;
    }

    // If user hasn't explicitly selected an existing item (no item_id) but SKU or barcode matches an existing one, block
    if (!entryRow.item_id && matchedItem) {
      if (skuTerm && matchedItem.sku.toLowerCase() === skuTerm) {
        setConflictSku(matchedItem.sku);
        toast.error(`SKU already exists as "${matchedItem.name}". Choose that item or use a different SKU.`);
        return;
      }
      if (barcodeTerm && (matchedItem.barcode || '').toLowerCase() === barcodeTerm) {
        setConflictBarcode(matchedItem.barcode || '');
        toast.error(`Barcode already exists for "${matchedItem.name}".`);
        return;
      }
    }

    const resolvedName = entryRow.item_name.trim() || matchedItem?.name || '';
    if (!resolvedName) {
      toast.error('Item name is required');
      return;
    }
    if (entryRow.quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    const isNew = !matchedItem;
    const itemId = matchedItem?.id;
    const buyPrice = entryRow.buy_price > 0 ? entryRow.buy_price : matchedItem?.buying_price || 0;
    const sellPrice = entryRow.sell_price > 0 ? entryRow.sell_price : matchedItem?.selling_price || 0;
    const skuValue = manualSku && !isLegacyAutoSku(manualSku) ? manualSku : fallbackSku;
    const barcodeValue = entryRow.barcode.trim() || matchedItem?.barcode || '';
    const vatEnabledValue = entryRow.item_id
      ? entryRow.vat_enabled
      : (entryRow.vat_enabled || Boolean(matchedItem?.vat_enabled));

    const existingIndex = itemId ? lineItems.findIndex(li => li.item_id === itemId) : -1;
    if (!isNew && existingIndex >= 0) {
      const updated = [...lineItems];
      updated[existingIndex].quantity += entryRow.quantity;
      updated[existingIndex].buy_price = buyPrice;
      updated[existingIndex].sell_price = sellPrice;
      updated[existingIndex].barcode = barcodeValue;
      updated[existingIndex].vat_enabled = vatEnabledValue;
      updated[existingIndex].total = computeLineTotal(
        updated[existingIndex].quantity,
        updated[existingIndex].buy_price,
        updated[existingIndex].vat_enabled
      );
      setLineItems(updated);
    } else {
      const newLineItem: InvoiceLineItem = {
        id: crypto.randomUUID(),
        item_id: itemId,
        item_name: resolvedName,
        item_sku: skuValue,
        barcode: barcodeValue,
        vat_enabled: vatEnabledValue,
        quantity: entryRow.quantity,
        buy_price: buyPrice,
        sell_price: sellPrice,
        total: computeLineTotal(entryRow.quantity, buyPrice, vatEnabledValue),
        is_new: isNew
      };
      setLineItems(prev => [...prev, newLineItem]);
    }

    resetEntryRow();
    setTimeout(() => itemNameInputRef.current?.focus(), 0);
  };

  const handleEntryKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const hasSuggestions = Boolean(entryActiveField && entryQuery && entrySuggestions.length > 0);
    if (!hasSuggestions) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (entryActiveField === 'barcode') {
          const matchedItem = findMatchedItem();
          if (matchedItem) {
            setEntryFromItem(matchedItem);
            setEntryActiveField('barcode');
          }
          return;
        }
        handleCommitEntryRow();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSuggestionIndex((prev) => (prev + 1) % entrySuggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSuggestionIndex((prev) => (prev - 1 + entrySuggestions.length) % entrySuggestions.length);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const selected = entrySuggestions[suggestionIndex];
      if (selected) {
        setEntryFromItem(selected);
        if (entryActiveField === 'barcode') {
          setEntryActiveField('barcode');
          return;
        }
        setEntryActiveField(null);
        setTimeout(() => itemNameInputRef.current?.focus(), 0);
        return;
      }
      if (entryActiveField === 'barcode') {
        const matchedItem = findMatchedItem();
        if (matchedItem) {
          setEntryFromItem(matchedItem);
          setEntryActiveField('barcode');
        }
        return;
      }
      handleCommitEntryRow();
    }
  };

  // Update line item
  const handleUpdateLineItem = (id: string, field: keyof InvoiceLineItem, value: number | string | boolean) => {
    const updated = lineItems.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'buy_price' || field === 'vat_enabled') {
          const nextQuantity = field === 'quantity' ? safeNumber(value as number) : safeNumber(updatedItem.quantity);
          const nextBuyPrice = field === 'buy_price' ? safeNumber(value as number) : safeNumber(updatedItem.buy_price);
          const nextVat = field === 'vat_enabled' ? Boolean(value) : Boolean(updatedItem.vat_enabled);
          updatedItem.total = computeLineTotal(nextQuantity, nextBuyPrice, nextVat);
        }
        return updatedItem;
      }
      return item;
    });
    setLineItems(updated);
  };

  // Remove line item
  const handleRemoveLineItem = (id: string) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  // Generate PDF without selling price
  const generateInvoicePDF = async () => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (!supplier) return;

    const doc = new jsPDF();
    let yOffset = 15;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('SUPPLIER INVOICE', 105, yOffset, { align: 'center' });
    yOffset += 15;

    // Invoice details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${invoiceNumber || 'TBD (Auto-generated)'}`, 14, yOffset);
    doc.text(`Date: ${invoiceDate}`, 140, yOffset);
    yOffset += 6;
    doc.text(`Supplier: ${supplier.name}`, 14, yOffset);
    doc.text(`Due Date: ${dueDate}`, 140, yOffset);
    yOffset += 6;
    if (supplier.phone) {
      doc.text(`Phone: ${supplier.phone}`, 14, yOffset);
      yOffset += 6;
    }
    if (supplier.address) {
      doc.text(`Address: ${supplier.address}`, 14, yOffset);
      yOffset += 6;
    }
    yOffset += 5;

    // Items table (without selling price)
    const tableData = lineItems.map(item => [
      item.item_name,
      item.item_sku,
      item.quantity.toString(),
      `${currencySymbol}${item.buy_price.toFixed(2)}`,
      `${currencySymbol}${item.total.toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: yOffset,
      head: [['Item Name', 'SKU', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [66, 66, 66] },
      foot: [
        ['', '', '', 'Subtotal:', `${currencySymbol}${invoiceSubtotal.toFixed(2)}`],
        ['', '', '', 'Transportation:', `${currencySymbol}${transportationCostAmount.toFixed(2)}`],
        ['', '', '', 'Grand Total:', `${currencySymbol}${invoiceTotal.toFixed(2)}`]
      ],
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    // Save PDF
    doc.save(`Invoice-${invoiceNumber || 'preview'}.pdf`);
    toast.success('PDF generated successfully');
  };

  // Generate invoice number: XXX-INV-YYYYMMDD-XXXX
  const generateInvoiceNumber = () => {
    const supplier = suppliers.find(s => s.id === supplierId);
    const supplierPrefix = supplier ? supplier.name.substring(0, 3).toUpperCase() : 'SUP';
    const dateStr = format(new Date(), 'yyyyMMdd');
    const randomSuffix = Math.random().toString(16).substring(2, 6).toUpperCase();
    setInvoiceNumber(`${supplierPrefix}-INV-${dateStr}-${randomSuffix}`);
  };

  // Submit invoice
  const handleSubmit = async () => {
    // Validation
    if (!supplierId) {
      toast.error('Please select a supplier');
      return;
    }
    if (!invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }
    if (!invoiceDate) {
      toast.error('Invoice date is required');
      return;
    }
    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    if (lineItems.some(item => !item.item_id && !isLegacyAutoSku(item.item_sku) && item.item_sku.trim().length > SKU_MAX_LENGTH)) {
      toast.error('SKU must be 8 characters or fewer');
      return;
    }
    if (transportationCostAmount < 0) {
      toast.error('Transportation cost cannot be negative');
      return;
    }

    setLoading(true);
    setConflictSku(null);
    setConflictBarcode(null);
    try {
      // Refresh catalog before submit so duplicate checks use latest items data
      const itemsRes = await fetchItems();
      if (itemsRes?.data) {
        const refreshedItems = normalizeItems(itemsRes.data || []);
        setItems(refreshedItems);

        const draftPayload = lineItems.map(item => ({ ...item }));
        const catalogConflict = findCatalogConflicts(refreshedItems, draftPayload);
        const draftConflict = findDraftConflicts(draftPayload);

        if (catalogConflict) {
          if (catalogConflict.type === 'sku') {
            setConflictSku(catalogConflict.value);
            toast.error(`SKU already exists as "${catalogConflict.itemName}". Please use a different SKU.`);
          } else {
            setConflictBarcode(catalogConflict.value);
            toast.error(`Barcode already exists as "${catalogConflict.itemName}". Please use a different barcode.`);
          }
          return;
        }

        if (draftConflict) {
          if (draftConflict.type === 'sku') {
            setConflictSku(draftConflict.value);
            toast.error(`Duplicate SKU in invoice: "${draftConflict.value}". Please keep SKUs unique within the invoice.`);
          } else {
            setConflictBarcode(draftConflict.value);
            toast.error(`Duplicate barcode in invoice: "${draftConflict.value}". Please keep barcodes unique within the invoice.`);
          }
          return;
        }
      }

      const formData = new FormData();
      formData.append('supplier_id', supplierId);
      formData.append('invoice_number', invoiceNumber);
      formData.append('date', invoiceDate);
      formData.append('due_date', dueDate);
      formData.append('amount', invoiceTotal.toString());
      formData.append('transportation_cost', transportationCostAmount.toString());
      formData.append('branch_id', selectedBranch);
      formData.append('notes', notes);
      formData.append('items', JSON.stringify(lineItems.map(item => ({
        item_id: item.item_id || null,
        item_name: item.item_name,
        item_sku: item.item_sku,
        barcode: item.barcode || null,
        vat_enabled: item.vat_enabled ? 1 : 0,
        quantity: item.quantity,
        buy_price: item.buy_price,
        sell_price: item.sell_price,
        total_price: item.total,
        is_new: item.is_new
      }))));

      if (attachment) {
        formData.append('attachment', attachment);
      }

      await createSupplierInvoiceWithItems(formData);

      toast.success('Invoice created successfully!');
      resetForm();
      proceedWithoutPrompt(onSuccess);
    } catch (error: any) {
      console.error('Failed to create invoice:', error);
      const apiError = error.response?.data;

      const duplicateName = apiError?.existing_item || apiError?.existing_item_name || 'an existing item';

      const hasSessionError =
        error.response?.status === 401 ||
        (error.response?.status === 403 &&
          apiError?.error === 'Branch or admin access required');

      if (hasSessionError) {
        toast.error('Session expired or your permission changed. Please login again.');
        return;
      }

      if (apiError?.sku) {
        setConflictSku(apiError.sku);
        toast.error(`${apiError.error || 'SKU already exists'} (existing item: ${duplicateName})`);
        return;
      }

      if (apiError?.barcode) {
        setConflictBarcode(apiError.barcode);
        toast.error(`${apiError.error || 'Barcode already exists'} (existing item: ${duplicateName})`);
        return;
      }

      if (apiError?.error === 'Unauthorized') {
        toast.error('Session expired. Please login again.');
        return;
      }

      if (apiError?.error?.includes('Authorization') || apiError?.error?.includes('access')) {
        toast.error('Session or permission issue. Please login again.');
        return;
      }

      if (apiError?.error === 'Branch or admin access required') {
        toast.error('Session expired or your permission changed. Please login again.');
        return;
      }

      toast.error(apiError?.error || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSupplierId('');
    setInvoiceNumber('');
    setInvoiceDate(initialInvoiceDate);
    setDueDate('');
    setSelectedBranch('');
    setAttachment(null);
    setShowAttachment(false);
    setNotes('');
    setTransportationCost(0);
    setLineItems([]);
    resetEntryRow();
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${currencySymbol}${(num || 0).toFixed(2)}`;
  };

  return (
    <div className="space-y-3 w-full">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <h1 className="text-lg font-semibold">Add Supplier Invoice</h1>
        </div>
        <Label className="flex items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={showAttachment}
            onCheckedChange={(checked) => {
              const enabled = checked === true;
              setShowAttachment(enabled);
              if (!enabled) {
                setAttachment(null);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                setShowAttachment(prev => {
                  const next = !prev;
                  if (!next) {
                    setAttachment(null);
                  }
                  return next;
                });
              }
            }}
          />
          Attach invoice
        </Label>
      </div>

      <form
        className="space-y-3 w-full"
        autoComplete="off"
        onSubmit={(event) => event.preventDefault()}
      >
        <input
          type="text"
          name={`autocomplete_blocker_${autoFillNonce}`}
          value=""
          onChange={() => {}}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          className="hidden"
        />
          {/* Invoice Header */}
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
              <div>
                <Label className="text-xs">Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger className="h-7 text-sm">
                    <SelectValue placeholder="Select supplier..." />
                  </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {supplierId && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Payment Terms: {suppliers.find(s => s.id === supplierId)?.paymentTerm || 'N/A'}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Branch *</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="h-7 text-sm">
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name} {branch.is_main && '(Main)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Invoice Number *</Label>
              <div className="flex gap-2">
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Enter or generate"
                  className="h-7 text-sm flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={generateInvoiceNumber}
                  title="Generate invoice number"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Invoice Date *</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="h-7 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="h-7 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Transportation Cost</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={toNumberInputValue(transportationCost)}
                onChange={(e) => {
                  const value = e.target.value;
                  setTransportationCost(value === '' ? Number.NaN : parseFloat(value));
                }}
                className="h-7 text-sm"
                placeholder="0.00"
              />
              {transportationCostPerUnit > 0 && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Adds {formatCurrency(transportationCostPerUnit)} per unit
                </p>
              )}
            </div>
          </div>

          {showAttachment && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Attachment</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="h-7 text-sm"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                />
              </div>
            </div>
          )}
          </div>

          {/* Items Section */}
          <div className="border rounded-lg p-3 w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Invoice Items
              </h3>
              <Badge variant="outline" className="text-lg px-3 py-1">
                Total: {formatCurrency(invoiceTotal)}
              </Badge>
            </div>

            {/* Line Items Table */}
            <div className="border rounded-lg overflow-visible">
              <Table wrapperClassName="overflow-visible">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                      <TableHead className="w-[640px]">Item</TableHead>
                      <TableHead className="w-[170px]">SKU</TableHead>
                      <TableHead className="w-[170px]">Barcode</TableHead>
                      <TableHead className="text-center w-[90px]">VAT</TableHead>
                    <TableHead className="text-center w-[100px]">Qty</TableHead>
                    <TableHead className="text-right w-[120px]">Buy Price</TableHead>
                    <TableHead className="text-right w-[120px]">Sell Price</TableHead>
                    <TableHead className="text-right w-[140px]">Transport</TableHead>
                    <TableHead className="text-right w-[120px]">Total</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <>
                    <TableRow
                      className={cn(
                        (conflictSku || conflictBarcode) ? 'bg-red-50 ring-1 ring-red-200' : ''
                      )}
                    >
                      <TableCell className="relative">
                        <Input
                          ref={itemNameInputRef}
                          placeholder="Start typing item name..."
                          value={entryRow.item_name}
                          onChange={(e) => {
                            setEntryRow(prev => ({ ...prev, item_name: e.target.value, item_id: '' }));
                            setEntryActiveField('name');
                          }}
                          onFocus={() => setEntryActiveField('name')}
                          onKeyDown={handleEntryKeyDown}
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck={false}
                          autoCapitalize="none"
                          name={`invoice_item_name_${autoFillNonce}`}
                          id={`invoice_item_name_${autoFillNonce}`}
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-form-type="other"
                        />
                        {entryActiveField && entryQuery && entrySuggestions.length > 0 && (
                          <div className="absolute left-0 top-full z-50 mt-1 w-[920px] max-w-[90vw] rounded-md border bg-white shadow-lg">
                            <div ref={suggestionsRef} className="max-h-[240px] min-h-[240px] overflow-y-auto">
                              {displaySuggestions.map((item, index) => (
                                <button
                                  key={item?.id ?? `placeholder-${index}`}
                                  type="button"
                                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                                    index === suggestionIndex ? 'bg-slate-100' : 'hover:bg-slate-100'
                                  } ${item ? '' : 'text-slate-300 cursor-default'}`}
                                  data-suggestion-index={index}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    if (item) {
                                      setEntryFromItem(item);
                                    }
                                  }}
                                  disabled={!item}
                                >
                                  <div>
                                    <div className="font-medium">{item?.name ?? '—'}</div>
                                    <div className="text-xs text-slate-500">{item?.sku ?? ''}</div>
                                  </div>
                                  <div className="text-xs text-slate-500">{item ? formatCurrency(item.buying_price) : ''}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="SKU"
                          value={entryRow.item_sku}
                          maxLength={SKU_MAX_LENGTH}
                          onChange={(e) => {
                            setEntryRow(prev => ({ ...prev, item_sku: e.target.value, item_id: '' }));
                            setEntryActiveField('sku');
                            setConflictSku(null);
                          }}
                          onFocus={() => setEntryActiveField('sku')}
                      onKeyDown={handleEntryKeyDown}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      autoCapitalize="none"
                      className={cn(
                        "min-w-[140px]",
                        conflictSku && entryRow.item_sku.trim() === conflictSku ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''
                      )}
                          name={`invoice_item_sku_${autoFillNonce}`}
                          id={`invoice_item_sku_${autoFillNonce}`}
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-form-type="other"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Barcode"
                          value={entryRow.barcode}
                          onChange={(e) => {
                            setEntryRow(prev => ({ ...prev, barcode: e.target.value, item_id: '' }));
                            setEntryActiveField('barcode');
                            setConflictBarcode(null);
                          }}
                          onFocus={() => setEntryActiveField('barcode')}
                      onKeyDown={handleEntryKeyDown}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      autoCapitalize="none"
                      className={cn(
                        "min-w-[140px]",
                        conflictBarcode && entryRow.barcode.trim() === conflictBarcode ? 'border-red-400 ring-1 ring-red-300 bg-red-50' : ''
                      )}
                          name={`invoice_item_barcode_${autoFillNonce}`}
                          id={`invoice_item_barcode_${autoFillNonce}`}
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-form-type="other"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={entryRow.vat_enabled}
                          onCheckedChange={(checked) => setEntryRow(prev => ({ ...prev, vat_enabled: checked === true }))}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              setEntryRow(prev => ({ ...prev, vat_enabled: !prev.vat_enabled }));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="1"
                          className="w-20 mx-auto text-center"
                          value={entryRow.quantity}
                          onChange={(e) => setEntryRow(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                          onKeyDown={handleEntryKeyDown}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-24 ml-auto text-right"
                          value={toNumberInputValue(entryRow.buy_price)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEntryRow(prev => ({ ...prev, buy_price: value === '' ? Number.NaN : parseFloat(value) }));
                          }}
                          onKeyDown={handleEntryKeyDown}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-24 ml-auto text-right"
                          value={toNumberInputValue(entryRow.sell_price)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEntryRow(prev => ({ ...prev, sell_price: value === '' ? Number.NaN : parseFloat(value) }));
                          }}
                          onKeyDown={handleEntryKeyDown}
                        />
                      </TableCell>
                      <TableCell className="text-right text-sm text-gray-600">
                        {formatCurrency(safeNumber(entryRow.quantity) * transportationCostPerUnit)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(computeLineTotal(entryRow.quantity, entryRow.buy_price, entryRow.vat_enabled))}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCommitEntryRow}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {[...lineItems].reverse().map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {item.is_new && (
                              <Badge variant="secondary" className="text-xs">New</Badge>
                            )}
                            <span className="font-medium">{item.item_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{item.item_sku}</TableCell>
                        <TableCell>
                          <Input
                            value={item.barcode || ''}
                            onChange={(e) => handleUpdateLineItem(item.id, 'barcode', e.target.value)}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                            autoCapitalize="none"
                            className="min-w-[140px]"
                            name={`invoice_line_barcode_${item.id}_${autoFillNonce}`}
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-form-type="other"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={Boolean(item.vat_enabled)}
                            onCheckedChange={(checked) => handleUpdateLineItem(item.id, 'vat_enabled', checked === true)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleUpdateLineItem(item.id, 'vat_enabled', !item.vat_enabled);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            className="w-20 mx-auto text-center"
                            value={item.quantity}
                            onChange={(e) => handleUpdateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                            step="0.01"
                            className="w-24 ml-auto text-right"
                            value={toNumberInputValue(item.buy_price)}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleUpdateLineItem(item.id, 'buy_price', value === '' ? Number.NaN : parseFloat(value));
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-24 ml-auto text-right"
                            value={toNumberInputValue(item.sell_price)}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleUpdateLineItem(item.id, 'sell_price', value === '' ? Number.NaN : parseFloat(value));
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-right text-sm text-gray-600">
                          {formatCurrency(safeNumber(item.quantity) * transportationCostPerUnit)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(item.total)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveLineItem(item.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="rounded-lg border bg-gray-50 px-4 py-3">
            <div className="ml-auto grid max-w-sm grid-cols-2 gap-y-1 text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-right font-medium">{formatCurrency(invoiceSubtotal)}</span>
              <span className="text-gray-600">Transportation Cost</span>
              <span className="text-right font-medium">{formatCurrency(transportationCostAmount)}</span>
              <span className="text-base font-semibold">Total</span>
              <span className="text-right text-base font-semibold">{formatCurrency(invoiceTotal)}</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onCancel && confirmNavigation(onCancel)}
            disabled={loading}
          >
            Cancel
          </Button>
          {lineItems.length > 0 && (
            <Button
              variant="secondary"
              onClick={generateInvoicePDF}
              disabled={loading || !supplierId}
            >
              <FileText className="h-4 w-4 mr-2" />
              Preview PDF
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </div>
      </form>
      {confirmationDialog}
    </div>
  );
}

export function AddInvoiceDialog({ open, onOpenChange, onSuccess, suppliers }: AddInvoiceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[96vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">Add Supplier Invoice</DialogTitle>
          <DialogDescription className="sr-only">
            Create a supplier invoice with items, optional attachment, and branch inventory update.
          </DialogDescription>
        </DialogHeader>
        <AddInvoiceForm
          key={open ? 'open' : 'closed'}
          suppliers={suppliers}
          onSuccess={() => {
            onSuccess();
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
